import { Variant, Product, VariantServices } from "../../types/types";
import { Smush } from "../../types/helpers";
import { ListingContext } from "./getListingContext";
import {
  generateBreadcrumbs,
  generateDeliveryOptions,
  generatePageName,
} from "./helpers";
import { ensureIsActive } from "../product/ensureIsActive";
import { getDefaultCategory } from "../product/getDefaultCategory";
import { getPromotionalMessagesToShow } from "@/utils/product/getPromotionalMessagesToShow.ts";
import { getColourGroupForVariant } from "@/utils/product/getColourGroupForVariant.ts";
import { isPromotionalMessage } from "../product/getPromoMessaging";

type JLDataType = {
  recsModel: Record<string, never>;
  page: {
    locale: {
      country: "uk";
      currency: "gbp";
      language: "en-gb";
    };
    listing?: ListingContext;
    pageInfo: {
      breadCrumb: {
        clickable: number;
        value: string;
      }[];
      pageCanonicalURL: string;
      pageName: string;
      pageType: string;
      pageConfigName: string;
      site: "johnlewis.com";
    };
    productPages: {
      asaf: Record<string, never>;
      brand: string;
      category: string;
      colour: string;
      content: Record<string, never>;
      deliveryOptions: string;
      displayedSku: string;
      numberOfAvailableProductSkus: number;
      numberOfAvailableProductSkusDetails: string[];
      numberOfProductSkus: number;
      outOfStockProductSkus: number;
      outOfStockProductSkusDetails: string[];
      price: string;
      productId: string;
      productName: string;
      productLifecycle: "retired product" | "";
      productViewType: "standard product" | "main product";
      promotionClassifications: {
        group: string;
        type: string;
      }[];
      promotions: string;
      reviews: {
        averageRating: number;
        totalNumber: number;
      };
      services: VariantServices;
      size: string;
    };
  };
};

function filterAvailability(variants: Variant[], isAvailable = false) {
  return variants
    .filter(
      (variant) => !!variant.availability?.availableToOrder === isAvailable,
    )
    .map((variant) => variant.aliases.skuId);
}

export const generatePageLoadAnalyticsDataLayer = (
  product: Smush<Product>,
  variant: Variant,
  isSelected: boolean,
  pageConfigName?: string,
  listing?: ListingContext,
): JLDataType => {
  const variants: Variant[] = product.variants;
  const availableProductSkus = filterAvailability(variants, true);
  const outOfStockProductSkus = filterAvailability(variants, false);

  const variantGroup = product.variantGroups
    ? getColourGroupForVariant(product.variantGroups, variant.id)
    : undefined;

  const promotionalMessagesToShow = getPromotionalMessagesToShow(
    product,
    variant,
    isSelected,
    variantGroup,
  ).filter(isPromotionalMessage);

  return {
    recsModel: {},
    page: {
      locale: {
        country: "uk",
        currency: "gbp",
        language: "en-gb",
      },
      ...(listing !== undefined && { listing }),
      pageInfo: {
        breadCrumb: generateBreadcrumbs(product),
        pageCanonicalURL: `${window.location.protocol}//${window.location.host}${product.canonicalURL}`,
        pageName: generatePageName(product),
        pageType: `pdp scaffold product`,
        pageConfigName: `${pageConfigName || ""}`,
        site: "johnlewis.com",
      },
      productPages: {
        asaf: {},
        brand: product.brand?.name || "",
        category: getDefaultCategory(product) || "",
        colour: variant.differentiators?.colour || "",
        content: {},
        deliveryOptions: generateDeliveryOptions(product),
        displayedSku: variant.aliases.skuId,
        numberOfAvailableProductSkus: availableProductSkus.length,
        numberOfAvailableProductSkusDetails: availableProductSkus,
        numberOfProductSkus: variants.length,
        outOfStockProductSkus: outOfStockProductSkus.length,
        outOfStockProductSkusDetails: outOfStockProductSkus,
        price: variant.price?.value || "",
        productId: product.aliases.pimProductId,
        productName: product.title,
        productLifecycle: ensureIsActive(product) ? "" : "retired product",
        productViewType: "main product",
        promotionClassifications: promotionalMessagesToShow.map((message) => ({
          group: message.promotionGroup,
          type: message.promotionType,
        })),
        promotions: promotionalMessagesToShow[0]?.title.toLowerCase() || "",
        reviews: product.reviewSummary
          ? {
              averageRating: product.reviewSummary.averageRating,
              totalNumber: product.reviewSummary.numberOfReviews,
            }
          : {
              averageRating: 0,
              totalNumber: 0,
            },
        services: variant.services || [],
        size: variant.differentiators?.size || "",
      },
    },
  };
};
